import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * PRODUCT UPDATES, decided for THIS store.
 *
 * There is one channel for every store, but who hears it is chosen store by
 * store (Alex, 2026-10-27) — a store knows who should hear from us better
 * than one rule stretched across all of them. It was derived platform-wide in
 * qualifiesForUpdatesChannel() before, so nobody could see the rule, and an
 * earlier pass of this build made it one global setting, which was the same
 * mistake with a nicer surface.
 *
 * The default is the OWNER, and only the owner. A store with no saved choice
 * is not "nobody" — it has never been decided, so the owner is ticked and the
 * line says so.
 */

const ROLE_WORDS = {
  owner: 'Owner',
  store_admin: 'Store admin',
  sales_admin: 'Team member',
};

const initials = (name) => (name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0].toUpperCase())
  .join('');

const IMegaphone = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 11.5 19 5v14L4 12.5z" />
    <path d="M8 13v4a2 2 0 0 0 4 0v-2.5" />
  </svg>
);

export function StoreUpdates({ storeId }) {
  const [state, setState] = useState({ loading: true, candidates: [], ownerId: null, decided: false });
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    if (!storeId) return;
    setState((s) => ({ ...s, loading: true }));
    setError('');
    try {
      const { data } = await axios.get('/api/roam-admin/updates-channel/members', {
        params: { store_id: storeId },
        metadata: { silent: true },
      });
      setState({
        loading: false,
        candidates: data?.candidates || [],
        ownerId: data?.owner_id ?? null,
        decided: !!data?.decided,
      });
      setPicked((data?.member_ids || []).map(Number));
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not read who gets Product Updates.');
      setState((s) => ({ ...s, loading: false }));
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setPicked((prev) => (
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  ));

  const save = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await axios.put('/api/roam-admin/updates-channel/members', {
        store_id: storeId,
        user_ids: picked,
      });
      setNotice('Saved.');
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'That did not save.');
    } finally {
      setBusy(false);
    }
  };

  const chosen = state.candidates.filter((c) => picked.includes(c.id));

  const summary = () => {
    if (state.loading) return 'loading…';
    if (chosen.length === 0) return 'nobody on this store';
    const names = chosen.map((c) => (c.name || '').split(' ')[0]).filter(Boolean);
    const who = names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ');
    return state.decided ? who : `${who} · the default`;
  };

  return (
    <>
      <div className="rc-sc__chat">
        <span className="rc-sc__pinned-icon" aria-hidden="true"><IMegaphone /></span>
        <span className="rc-sc__chat-body">
          <span className="rc-sc__chat-name">Product Updates</span>
          <span className="rc-sc__chat-who">{summary()} · announcements from Retail Circle, read-only</span>
        </span>
      </div>

      {state.candidates.length > 0 && (
        <details className="rc-sc__fold">
          <summary>
            Who gets Product Updates
            <span className="rc-sc__fold-sub">
              {picked.length} of {state.candidates.length}
              {state.decided ? '' : ' · owner by default'}
            </span>
          </summary>
          <div className="rc-sc__fold-body">
            {error && <p className="rc-sc__error" role="alert">{error}</p>}
            {notice && <p className="rc-sc__hint" role="status">{notice}</p>}

            <div className="rc-sc__picks">
              {state.candidates.map((c) => {
                const on = picked.includes(c.id);

                return (
                  <label key={c.id} className={`rc-sc__pick ${on ? 'rc-sc__pick--on' : ''}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(c.id)} />
                    <span className="rc-sc__person-avatar" aria-hidden="true">{initials(c.name)}</span>
                    <span className="rc-sc__person-body">
                      <span className="rc-sc__person-name">{c.name || `User ${c.id}`}</span>
                      <span className="rc-sc__person-meta">
                        {ROLE_WORDS[c.role] || c.role}
                        {c.id === state.ownerId && !state.decided ? ' · included by default' : ''}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            <p className="rc-sc__hint">
              One channel for every store, but who hears it is decided store by store. Only Retail
              Circle master admins can post; everyone here reads it in Messages and cannot reply.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="rc-sc__btn rc-sc__btn--primary" disabled={busy} onClick={save}>
                {busy ? 'Saving…' : 'Save who gets it'}
              </button>
            </div>
          </div>
        </details>
      )}
    </>
  );
}

export default StoreUpdates;
